import React from 'react';

import { updateMetricByKey } from '@client/core/atoms/metrics.js';
import { updatePlacementKeyValueById } from '@client/core/atoms/placements.js';
import { $retailMediaAtom } from '@client/core/atoms/retailMedia.js';
import { Performance } from '@client/core/utils/Performance.js';
import { type DisplaySettings } from '@client/retailMedia/RetailMediaAdVendor.js';
import { i18n } from '@lingui/core';
import { useStore } from '@nanostores/react';
import {
  debugLog,
  PlacementStatus,
  type PlacementId
} from '@schibsted-nmp/advertising-shared';
import {
  BapSearchAd,
  AdImage
} from '@schibsted-nmp/search-frontend-components/react';

import type { RetailMediaProduct } from '../types/index.js';
import {
  trackClickBackend,
  trackImpressionBackend,
  trackViewableImpressionBackend
} from '../utils/RetailMediaApi.js';
import { SponsoredProductAdPulseTracker } from '../utils/RetailMediaPulse.js';

export function SponsoredProductAd({
  displaySettings,
  products,
  placementId,
  categories
}: SponsoredProductAd.Props) {
  const product = products[0];

  if (!placementId) {
    debugLog('retail_media -> Render failed due to missing placementId');
    return null;
  }

  if (!product) {
    debugLog(
      'retail_media -> Render failed due to missing product for placement:',
      placementId
    );

    updateMetricByKey(placementId, PlacementStatus.AdNoFill);
    updatePlacementKeyValueById(placementId, 'status', 'error');

    return null;
  }

  return (
    <SponsoredProductAd.Inner
      displaySettings={displaySettings}
      product={product}
      placementId={placementId}
      categories={categories}
    />
  );
}

export namespace SponsoredProductAd {
  export type Props = {
    displaySettings?: DisplaySettings | undefined;
    onClick?: () => void;
    products: Array<RetailMediaProduct>;
    placementId?: PlacementId;
    categories?: string[] | undefined;
  };

  export function Inner({
    displaySettings,
    product,
    placementId,
    categories
  }: Inner.Props) {
    const { layout, aspectClass } = useExtractLayoutAndAspect(displaySettings);
    const adRef = React.useRef<HTMLDivElement>(null);
    const bapAd = React.useMemo(() => convertToBapFormat(product), [product]);
    const { locale } = i18n;

    const IMAGE_SIZES =
      '(min-width: 1300px) 306px, (min-width: 1010px) 316px, (min-width: 768px) 33vw, (min-width: 480px) 50vw, 100vw';

    useImpression(adRef, product, placementId, categories);
    useViewableImpression(adRef, product, placementId, categories);

    // Workaround for Finnish decimal separator until BapSearchAd supports locale formatting
    useFormatFinnishDecimalSeparator(adRef, product);

    const handleClick = useTrackClick(product, categories, placementId);

    return (
      <>
        <div ref={adRef} lang={locale}>
          <BapSearchAd
            ad={bapAd}
            id={bapAd.ad_id}
            imageSizes={IMAGE_SIZES}
            layout={layout}
            url={bapAd.canonical_url}
            target="_blank"
            onClick={handleClick}
            className={`sponsored-product-ad sponsored-product-ad--${layout}`}
            label={() => (
              <Label
                product={product}
                categories={categories}
                placementId={placementId}
              />
            )}
            image={
              layout === 'grid'
                ? () => (
                    <AdImage
                      aspectClass={aspectClass}
                      height={bapAd.image.height}
                      width={bapAd.image.width}
                      url={bapAd.image.url}
                      crop={true}
                      sizes={IMAGE_SIZES}
                    />
                  )
                : undefined
            }
          />
        </div>
      </>
    );
  }

  export namespace Inner {
    export type Props = {
      displaySettings: DisplaySettings | undefined;
      product: RetailMediaProduct;
      placementId: PlacementId;
      categories: string[] | undefined;
    };
  }

  function Label({ product, categories, placementId }: Label.Props) {
    const handleDsaClick = useTrackDsaClick(product, categories, placementId);

    return (
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          pointerEvents: 'none',
          zIndex: 2
        }}
      >
        <span
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            fontSize: 'var(--w-font-size-xs)',
            lineHeight: 'var(--w-line-height-xs)',
            borderRadius: '0 0 4px 0',
            padding: '.4rem .8rem',
            backgroundColor: '#FFF',
            color: 'var(--w-s-color-text)'
          }}
        >
          {product.advertisingLabel}
        </span>

        <a
          href={product.dsaUrl}
          target="_blank"
          title="Ad info"
          aria-label="Ad info"
          onClick={handleDsaClick}
          style={{
            position: 'absolute',
            top: 0,
            right: 0,
            display: 'flex',
            width: '20px',
            height: '24px',
            padding: '4px 2px',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            borderRadius: '0 0 0 4px',
            background: '#FFF',
            border: 'none',
            cursor: 'pointer',
            pointerEvents: 'auto'
          }}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
          >
            <path
              d="M8 4C7.30964 4 6.75 3.44036 6.75 2.75C6.75 2.05964 7.30964 1.5 8 1.5C8.69036 1.5 9.25 2.05964 9.25 2.75C9.25 3.44036 8.69036 4 8 4Z"
              fill="#1B1B1F"
            />
            <path
              d="M8 9.25C7.30964 9.25 6.75 8.69036 6.75 8C6.75 7.30964 7.30964 6.75 8 6.75C8.69036 6.75 9.25 7.30964 9.25 8C9.25 8.69036 8.69036 9.25 8 9.25Z"
              fill="#1B1B1F"
            />
            <path
              d="M8 14.5C7.30964 14.5 6.75 13.9404 6.75 13.25C6.75 12.5596 7.30964 12 8 12C8.69036 12 9.25 12.5596 9.25 13.25C9.25 13.9404 8.69036 14.5 8 14.5Z"
              fill="#1B1B1F"
            />
          </svg>
        </a>
      </div>
    );
  }

  namespace Label {
    export type Props = {
      product: RetailMediaProduct;
      categories: string[] | undefined;
      placementId: PlacementId;
    };
  }

  function useExtractLayoutAndAspect(
    displaySettings: DisplaySettings | undefined
  ) {
    // Subscribe to atom for layout changes to re-render component as grid/list
    const retailMediaState = useStore($retailMediaAtom);

    const layout =
      retailMediaState.layoutType ?? displaySettings?.layoutType ?? 'grid';

    // Extract aspectFormat from atom (with fallback to displaySettings)
    const aspectFormat =
      retailMediaState.aspectFormat ??
      displaySettings?.aspectFormat ??
      'portrait';

    // Convert aspectFormat to CSS class
    const aspectClass =
      aspectFormat === 'landscape' ? 'aspect-4/3' : 'aspect-3/4';

    return { layout, aspectClass };
  }

  function useTrackDsaClick(
    product: RetailMediaProduct,
    categories: string[] | undefined,
    placementId: PlacementId
  ) {
    return React.useCallback(
      (event: React.MouseEvent<HTMLAnchorElement>) => {
        // The DSA link sits inside the ad card, so without this the card's own
        // handler counts opening the ad info page as a click on the product
        event.stopPropagation();

        void SponsoredProductAdPulseTracker.instance.trackInfoPageClick({
          product,
          zone: 'recommerce_search',
          categories,
          placementId
        });
      },
      [product, placementId, categories]
    );
  }

  function useTrackClick(
    product: RetailMediaProduct,
    categories: string[] | undefined,
    placementId: PlacementId
  ) {
    return React.useCallback(() => {
      // Track click server-side to avoid ad blockers
      void trackClickBackend(product.clickUrl);

      void SponsoredProductAdPulseTracker.instance.trackClick({
        product,
        zone: 'recommerce_search',
        categories,
        placementId
      });
    }, [product, placementId, categories]);
  }

  function useFormatFinnishDecimalSeparator(
    adRef: React.RefObject<HTMLDivElement>,
    product: RetailMediaProduct
  ) {
    React.useEffect(() => {
      if (!adRef.current) return;
      if (i18n.locale !== 'fi') return;

      const priceSpan = adRef.current.querySelector(
        '.sponsored-product-ad div.text-m.font-bold.whitespace-nowrap > span'
      );

      if (priceSpan?.textContent) {
        priceSpan.textContent = priceSpan.textContent.replace('.', ',');
      }
    }, [adRef, product]);
  }

  function useViewableImpression(
    adRef: React.RefObject<HTMLDivElement>,
    product: RetailMediaProduct,
    placementId: PlacementId,
    categories: string[] | undefined
  ) {
    const hasTrackedViewableImpression = React.useRef(false);

    const viewableImpressionHasTriggered = Performance.useViewableImpression({
      elementRef: adRef
    });

    React.useEffect(() => {
      if (hasTrackedViewableImpression.current) return;
      if (!viewableImpressionHasTriggered) return;

      hasTrackedViewableImpression.current = true;

      updateMetricByKey(placementId, PlacementStatus.AdViewed);

      // TODO: Ensure viewable slots are rendered the same as loaded slots before enabling this:
      // updatePlacementKeyValueById(placementId, 'status', 'viewable');

      if (product.viewableImpressionUrl) {
        // Track viewable impression server-side to avoid ad blockers
        void trackViewableImpressionBackend(product.viewableImpressionUrl);
      }

      void SponsoredProductAdPulseTracker.instance.trackViewableImpression({
        product,
        zone: 'recommerce_search',
        categories,
        placementId
      });
    }, [viewableImpressionHasTriggered, product, placementId]);
  }

  function useImpression(
    adRef: React.RefObject<HTMLDivElement>,
    product: RetailMediaProduct,
    placementId: PlacementId,
    categories: string[] | undefined
  ) {
    const hasTrackedImpression = React.useRef(false);

    const impressionHasTriggered = Performance.useImpression({
      elementRef: adRef
    });

    React.useEffect(() => {
      if (hasTrackedImpression.current) return;
      if (!impressionHasTriggered) return;

      hasTrackedImpression.current = true;

      updateMetricByKey(placementId, PlacementStatus.AdLoaded);
      updatePlacementKeyValueById(placementId, 'status', 'loaded');

      // Track impression server-side to avoid ad blockers
      void trackImpressionBackend(product.impressionUrl);

      void SponsoredProductAdPulseTracker.instance.trackImpression({
        product,
        zone: 'recommerce_search',
        categories,
        placementId
      });
    }, [impressionHasTriggered, product, placementId]);
  }

  function optimizeImageUrl(url: string): string {
    const params = new URLSearchParams({
      w: '320',
      q: '30',
      f: 'webp'
    });
    return `${url}?${params.toString()}`;
  }

  function convertToBapFormat(product: RetailMediaProduct) {
    return {
      type: 'bap',
      ad_type: 67,
      ad_id: product.adId,
      canonical_url: product.productUrl,
      distance: null,
      brand: product.brand,
      extras: [
        {
          id: 'brand',
          label: '',
          values: [product.brand]
        }
      ],
      flags: ['SPONSORED'],
      heading: product.title,
      image: {
        aspect_ratio: 1.3333333333333333,
        height: 400,
        url: optimizeImageUrl(product.imageUrl),
        width: 533
      },
      labels: [],
      location: '',
      main_search_key: 'SEARCH_ID_BAP_COMMON',
      organisation_name: product.advertiser,
      price: {
        currency_code: product.currency,
        amount: product.price,
        price_unit: product.priceUnit
      },
      timestamp: undefined
    };
  }
}
